/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import SurveyOverview from './overview';
import React, { useContext, useEffect } from 'react';
import { SurveyContext } from '@features/survey-container/SurveyContainer';
import { useGetAggregatedAsyncProcessesQuery } from '@api/aggregated-surveys';
import { WorkInProgress } from '@g17eco/molecules/work-in-progress';
import { SURVEY } from '@constants/terminology';
import { AsyncProcessType } from '@g17eco/types/survey';
import { hasNoProcessingProcess, hasProcessingProcess } from '@utils/survey';

const POLLING_INTERVAL_MS = 10000; // 10 seconds

export const SurveyOverviewWithBlockLoading = (props: React.ComponentProps<typeof SurveyOverview>) => {
  const { surveyData, isLoaded } = useContext(SurveyContext);

  const hasProcessingSummary = hasProcessingProcess(surveyData?.asyncProcesses, AsyncProcessType.AISummary);
  const hasRequiredData = isLoaded && surveyData?._id && surveyData?.initiativeId;
  const shouldPollStatus = hasRequiredData && hasProcessingSummary;

  const { data: asyncProcesses } = useGetAggregatedAsyncProcessesQuery(
    {
      initiativeId: surveyData?.initiativeId || '',
      surveyId: surveyData?._id || '',
    },
    {
      skip: !shouldPollStatus,
      pollingInterval: hasProcessingSummary ? POLLING_INTERVAL_MS : undefined,
    },
  );

  // Reload page when survey processing is complete
  useEffect(() => {
    if (hasNoProcessingProcess(asyncProcesses)) {
      window.location.reload();
    }
  }, [asyncProcesses]);

  if (isLoaded && hasProcessingSummary) {
    return (
      <div data-testid='survey-overview-processing'>
        <WorkInProgress
          title={
            <>
              <div className='mb-2'>Your {SURVEY.SINGULAR} is being created...</div>
              <div className='text-muted'>This may take a few minutes.</div>
            </>
          }
        />
      </div>
    );
  }

  return <SurveyOverview {...props} />;
};
